import { useCallback, useMemo } from 'react';
import cn from 'classnames';
import { DeleteOutlined } from '@ant-design/icons';
import { Popconfirm, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import {
  BlacklistRequest,
  getBlacklist,
  removeFromBlacklist,
} from '../../../../store/reducers/BlacklistSlice';
import { ReasonTypes } from './types';
import { REASONS_MAP_TEXT } from './constants';
import { BlacklistTablet } from '../../../../models/Blacklist/Blacklist';
import styles from './style.module.css';
import { useAppDispatch } from '../../../../store/store';
import { blacklistSortMap } from '../../../shared/journalSortMaps';
import { withServerSorter } from '../../../shared/useServerTableSort';

interface Props {
  callRequest: BlacklistRequest;
  disabled?: boolean;
}

const UNBLOCK_CONFIRM_TEXT =
  'Номер будет разблокирован и доступен для обзвона. Удалить?';

export const useBlacklistColumns = ({
  callRequest,
  disabled = false,
}: Props) => {
  const dispatch = useAppDispatch();

  const handleUnblock = useCallback(
    (phone: string) => {
      if (disabled) return;

      dispatch(removeFromBlacklist(phone)).then(() => {
        dispatch(getBlacklist(callRequest));
      });
    },
    [callRequest, disabled, dispatch],
  );

  const baseColumns: ColumnsType<BlacklistTablet> = useMemo(
    () => [
      {
        key: 'created',
        title: (
          <div className={cn(styles.tabletTitle, styles.titleTime)}>
            <p>Дата добавления</p>
          </div>
        ),
        dataIndex: 'created',
        responsive: ['sm'],
        align: 'center' as const,
        width: 170,
        render: (text: string) => (
          <div className={styles.tableCell} data-group="blacklistDate">
            <span
              className={styles.tabletText}
              dangerouslySetInnerHTML={{ __html: text }}
            />
          </div>
        ),
      },
      {
        key: 'phone',
        title: <div className={styles.tabletTitle}>Телефон</div>,
        dataIndex: 'phone',
        responsive: ['sm'],
        align: 'center' as const,
        width: 190,
        render: (text: string) => (
          <div className={styles.tableCell}>
            <span
              className={styles.tabletText}
              style={{ whiteSpace: 'nowrap' }}
            >
              {text}
            </span>
          </div>
        ),
        onHeaderCell: () => ({
          style: { fontSize: '18px' },
        }),
      },
      {
        title: <div className={styles.tabletTitle}>Причина</div>,
        dataIndex: 'reason',
        responsive: ['sm'],
        align: 'center' as const,
        width: 260,
        render: (value: ReasonTypes) => {
          const text = REASONS_MAP_TEXT[value];

          return (
            <div className={styles.tableCell}>
              <span className={styles.tabletText}>{text}</span>
            </div>
          );
        },
        onHeaderCell: () => ({
          style: { fontSize: '18px' },
        }),
      },
      {
        key: 'comment',
        title: <div className={styles.tabletTitle}>Комментарий</div>,
        dataIndex: 'comment',
        responsive: ['sm'],
        align: 'center' as const,
        width: 260,
        render: (text: string) => (
          <div className={styles.tableCell}>
            <span className={styles.tabletText}>{text}</span>
          </div>
        ),
        onHeaderCell: () => ({
          style: { fontSize: '18px' },
        }),
      },
      {
        title: '',
        dataIndex: 'actions',
        responsive: ['sm'],
        align: 'center' as const,
        width: 100,
        onHeaderCell: () => ({
          style: { fontSize: '18px' },
        }),
        render: (_value: unknown, record: BlacklistTablet) => (
          <div
            className={cn(
              styles.tableCell,
              styles.tableCellLast,
              styles.actionCell,
            )}
          >
            <Tooltip title="Удалить из черного списка">
              <Popconfirm
                title={UNBLOCK_CONFIRM_TEXT}
                onConfirm={() => handleUnblock(record.phone)}
                disabled={disabled}
              >
                <div className={styles.actionButton} aria-disabled={disabled}>
                  <span className={styles.actionIconWrapper}>
                    <DeleteOutlined className={styles.actionIcon} />
                  </span>
                </div>
              </Popconfirm>
            </Tooltip>
          </div>
        ),
      },
    ],
    [disabled, handleUnblock],
  );

  const COLUMNS = useMemo(
    () => withServerSorter(baseColumns, callRequest, blacklistSortMap),
    [baseColumns, callRequest],
  );

  return { COLUMNS };
};
